import {AfterViewInit, Component, OnInit, ViewChild} from '@angular/core';
import {PatternListDto} from '../../dtos/text/pattern-list-dto';
import {TextPatternService} from '../../services/text/text-pattern.service';
import {NotificationService} from '../../services/common/notification.service';
import {Router} from '@angular/router';
import {AuthService} from '../../services/auth/auth.service';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import {HttpParams} from '@angular/common/http';
import {merge} from 'rxjs';
import {map, startWith, switchMap} from 'rxjs/operators';
import {MatDialog} from "@angular/material/dialog";
import {ConfirmationDialogComponent} from "../common/confirmation-dialog/confirmation-dialog.component";
import {TextPattern} from "../../dtos/text/text-pattern";
import {NewTextPatternComponent} from "./new-text-pattern/new-text-pattern.component";
import {TextPatternCreationDto} from "../../dtos/text/text-pattern-dto";
import {FolderActionsService} from "../../services/folder/folder-actions.service";
import {FolderService} from "../../services/folder/folder.service";
import {CurrentUserService} from "../../services/user/current-user.service";
import {folderUsageText} from "../../shared/folder/folder-utils";
import {catchError, map as mapResult} from "rxjs/operators";
import {Observable, of} from "rxjs";

@Component({
  selector: 'app-text-pattern',
  templateUrl: './text-pattern.component.html',
  styleUrls: ['./text-pattern.component.scss'],
  standalone: false
})
export class TextPatternComponent implements OnInit, AfterViewInit {

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  textPatterns: PatternListDto[] = [];
  displayedColumns = ['name', 'mode', 'details'];
  resultsLength = 20;
  filterOral: boolean;
  filterWritten: boolean;

  isAuthorized: boolean;
  isAdmin: boolean;
  displayEditingTools = false;

  // checkboxes to add entries to a Sammelmappe, off until the user asks for them
  selectMode = false;

  toggleSelectMode(): void {
    this.selectMode = !this.selectMode;
    if (!this.selectMode) {
      this.selectedIds.clear();
    }
  }

  // entries selected to be added to a Sammelmappe, kept across pages
  selectedIds = new Set<number>();
  canUseFolders = false;

  constructor(private textPatternService: TextPatternService,
              private notificationService: NotificationService,
              private router: Router, private dialog: MatDialog,
              public authService: AuthService,
              private matDialog: MatDialog,
              private folderActions: FolderActionsService,
              private folderService: FolderService,
              private currentUserService: CurrentUserService) {
  }

  ngOnInit() {
    [this.isAuthorized, this.isAdmin] = this.authService.isAuthorizedAndAdmin();
    this.canUseFolders = this.currentUserService.canUseFolders();
  }

  get columns(): string[] {
    return this.selectMode && this.canUseFolders ? ['select', ...this.displayedColumns] : this.displayedColumns;
  }

  isPageSelected(): boolean {
    return this.textPatterns.length > 0 && this.textPatterns.every(p => this.selectedIds.has(p.id));
  }

  isPagePartlySelected(): boolean {
    return this.textPatterns.some(p => this.selectedIds.has(p.id)) && !this.isPageSelected();
  }

  togglePage(checked: boolean): void {
    this.textPatterns.forEach(p => checked ? this.selectedIds.add(p.id) : this.selectedIds.delete(p.id));
  }

  toggleSelection(id: number, checked: boolean): void {
    if (checked) {
      this.selectedIds.add(id);
    } else {
      this.selectedIds.delete(id);
    }
  }

  addSelectionToFolder(): void {
    const items = [...this.selectedIds].map(id => ({type: 'TEXT_PATTERN' as const, id}));
    this.folderActions.addToFolder(items).subscribe(() => this.selectedIds.clear());
  }


  ngAfterViewInit() {
    this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0);
    merge(this.sort.sortChange, this.paginator.page)
      .pipe(
        startWith({}),
        switchMap(() => {
          let params = new HttpParams();
          if (String(this.sort.active) !== 'created') {
            params = params.append('order', String(this.sort.direction));
            params = params.append('sort', String(this.sort.active));
          }
          params = params.append('size', String(this.paginator.pageSize));
          params = params.append('page', String(this.paginator.pageIndex));
          return this.textPatternService?.searchTextPatterns(params);
        }),
        map(data => {
          this.resultsLength = data.totalElements;
          return data.content;
        })).subscribe(data => {
      this.textPatterns = data;
    }, error => {
      this.notificationService.openSnackBar(error);
    });
  }

  updateChipOral() {
    this.filterOral = !this.filterOral;
    if (this.filterWritten && this.filterOral) {
      this.filterWritten = !this.filterWritten;
    }
    this.search();
  }

  updateChipWritten() {
    this.filterWritten = !this.filterWritten;
    if (this.filterWritten && this.filterOral) {
      this.filterOral = !this.filterOral;
    }
    this.search();
  }

  search() {
    let params = new HttpParams();
    if (String(this.sort.active) !== 'created') {
      params = params.append('order', String(this.sort.direction));
      params = params.append('sort', String(this.sort.active));
    }
    params = params.append('size', String(this.paginator.pageSize));
    params = params.append('page', String(this.paginator.pageIndex));
    if (this.filterOral) {
      params = params.append('modes', 'ORAL');
    }
    if (this.filterWritten) {
      params = params.append('modes', 'WRITTEN');
    }
    this.textPatternService?.searchTextPatterns(params).subscribe(
      data => {
        this.resultsLength = data.totalElements;
        this.textPatterns = data.content;
      }, error => {
        this.notificationService.openSnackBar(error);
      });
  }

  translateMode(s: string) {
    if (s === 'ORAL') {
      return 'mündlich';
    }
    if (s === 'WRITTEN') {
      return 'schriftlich';
    }
    return s;
  }

  toggleEditingTools(change: boolean) {
    if (change) {
      this.displayEditingTools = !this.displayEditingTools;
    }
    if (this.displayEditingTools && this.isAdmin) {
      if (!this.displayedColumns.includes('delete')) {
        const lastIndex = this.displayedColumns.length - 1;
        this.displayedColumns.splice(lastIndex, 0, 'delete');
      }
    } else {
      this.displayedColumns = this.displayedColumns.filter(c => c !== 'delete');
    }
  }

  sendExportTextPatternsRequest() {
    this.textPatternService.exportAll().subscribe(x => {
      // It is necessary to create a new blob object with mime-type explicitly set
      // otherwise only Chrome works like it should
      const newBlob = new Blob([x.body], {type: 'text/csv'});
      const data = window.URL.createObjectURL(newBlob);

      const link = document.createElement('a');
      link.href = data;
      link.download = x.headers.get('content-disposition').split('filename=')[1];
      // this is necessary as link.click() does not work on the latest firefox
      link.dispatchEvent(new MouseEvent('click', {bubbles: true, cancelable: true, view: window}));

      setTimeout(function () {
        // For Firefox it is necessary to delay revoking the ObjectURL
        window.URL.revokeObjectURL(data);
        link.remove();
      }, 100);
    });
  }

  openRegisterPopup() {
    this.notificationService.openSnackBarWithAction('Bitte registrieren Sie sich kostenlos, um Details und Beispiele anzusehen!', 'Registrieren')
      .subscribe(() => {
        this.router.navigateByUrl('/login', {state: {register: true}});
      });
  }

  /**
   * opens a popup to create a new text pattern
   */
  openNewTextPatternDialog() {
     const dialogRef = this.matDialog.open(NewTextPatternComponent);
     dialogRef.afterClosed().subscribe(
        (result: TextPatternCreationDto) => {
          if (result && result.id) {
            void this.router.navigateByUrl('/textmuster/' + result.id);
          }
        }
     )
  }

  delete($event: TextPattern) {
    this.usageText($event.id).subscribe(usageText => this.confirmDelete($event, usageText));
  }

  /**
   * admin only: how many Sammelmappen contain the text pattern, as sentence for the delete dialog
   */
  private usageText(id: number): Observable<string> {
    return this.folderService.getUsage('TEXT_PATTERN', id).pipe(
      mapResult(usage => usage.folderCount > 0
        ? ' ' + folderUsageText(usage) + ' Der Eintrag wird daraus entfernt und die Nutzer werden benachrichtigt.'
        : ''),
      catchError(() => of(''))
    );
  }

  private confirmDelete($event: TextPattern, usageText: string) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        headline: 'Eintrag löschen',
        content:
          'Der Eintrag "' +
          $event.name +
          '" und alle dazugehörigen Daten werden gelöscht und können unter keinen Umständen ' +
          'wiederhergestellt werden.' + usageText + '\nWollen Sie den Eintrag wirklich löschen?',
        action: 'Löschen',
      },
    });

    dialogRef.afterClosed().subscribe({
      next: (confirmed) => {
        if (confirmed) {
          this.textPatternService.deletePattern($event.id).subscribe({
            next: () => {
              this.selectedIds.delete($event.id);
              this.search();
              this.notificationService.openSnackBar(
                'Eintrag ' + $event.name + ' wurde gelöscht.'
              );
            },
            error: (error) => {
              this.notificationService.openSnackBar(error);
            },
          });
        }
      },
    });
  }

}
